import React, { useState, useEffect, useCallback } from 'react';
import { X, Mail, RefreshCw, Eye, EyeOff, Send, AlertCircle, CheckCircle } from 'lucide-react';
import api from '../api/axios';

const EmailNotificationModal = ({ isOpen, onClose, event, onUpdate }) => {
  const [settings, setSettings] = useState({
    enable_email_notifications: false,
    notification_email_from_name: '',
    notification_email_from_address: '',
    notification_email_subject: '',
    notification_email_template: '',
    notification_max_retries: 3,
    notification_include_qr_code: true,
    notification_include_event_details: true,
  });
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [showTemplate, setShowTemplate] = useState(false);
  const [defaultTemplate, setDefaultTemplate] = useState('');
  const [availableVariables, setAvailableVariables] = useState([]);

  const loadSettings = useCallback(async () => {
    if (!event?.id) return;
    
    setLoading(true);
    try {
      const response = await api.get(`/events/${event.id}/email-notifications/settings`);
      if (response.data.success) {
        setSettings(response.data.data);
      }
    } catch (error) {
      console.error('Failed to load email notification settings:', error);
    } finally {
      setLoading(false);
    }
  }, [event?.id]);

  const loadDefaultTemplate = useCallback(async () => {
    if (!event?.id) return;
    
    try {
      const response = await api.get(`/events/${event.id}/email-notifications/default-template`);
      if (response.data.success) {
        setDefaultTemplate(response.data.data.template);
        setAvailableVariables(response.data.data.available_variables);
      }
    } catch (error) {
      console.error('Failed to load default template:', error);
    }
  }, [event?.id]);

  useEffect(() => {
    if (isOpen && event?.id) {
      loadSettings();
      loadDefaultTemplate();
    }
  }, [isOpen, event?.id, loadSettings, loadDefaultTemplate]);

  const handleSave = async () => {
    if (!event?.id) return;
    
    setSaving(true);
    try {
      const response = await api.put(`/events/${event.id}/email-notifications/settings`, settings);
      if (response.data.success) {
        onUpdate?.(response.data.data);
        onClose();
      }
    } catch (error) {
      console.error('Failed to save email notification settings:', error);
      alert('Failed to save settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmail) {
      alert('Please enter a test email address');
      return;
    }

    if (!event?.id) {
      alert('No event selected');
      return;
    }

    setSendingTest(true);
    setTestResult(null);
    
    try {
      const response = await api.post(`/events/${event.id}/email-notifications/test`, {
        test_email: testEmail
      });
      
      if (response.data.success) {
        setTestResult({ success: true, message: response.data.message });
      }
    } catch (error) {
      setTestResult({ 
        success: false, 
        message: error.response?.data?.message || 'Failed to send test email' 
      });
    } finally {
      setSendingTest(false);
    }
  };

  const useDefaultTemplate = () => {
    setSettings(prev => ({
      ...prev,
      notification_email_template: defaultTemplate
    }));
  };

  if (!isOpen) return null;

  // Don't render if no event is provided
  if (!event) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Email Settings</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>
          <div className="text-center py-4">
            <AlertCircle className="h-12 w-12 text-orange-500 mx-auto mb-3" />
            <p className="text-gray-600">No event selected. Please select an event first.</p>
            <button
              onClick={onClose}
              className="mt-4 px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-3">
            <Mail className="h-6 w-6 text-blue-600" />
            <h2 className="text-xl font-semibold text-gray-900">
              Email Notification Settings
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* Immediate Sending Notice */}
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center">
              <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
              <div>
                <h4 className="text-sm font-medium text-green-800">Immediate Email Delivery</h4>
                <p className="text-sm text-green-700 mt-1">
                  Emails are now sent immediately upon registration (like Google Forms). No delays or scheduling required!
                </p>
              </div>
            </div>
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin text-blue-600" />
              <span className="ml-2 text-gray-600">Loading settings...</span>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Enable/Disable Toggle */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Enable Email Notifications</h3>
                  <p className="text-sm text-gray-600">
                    Send confirmation emails to attendees after registration
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.enable_email_notifications}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      enable_email_notifications: e.target.checked
                    }))}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>

              {settings.enable_email_notifications && (
                <>
                  {/* Email Configuration Notice */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center space-x-2 mb-2">
                      <Mail className="h-5 w-5 text-blue-600" />
                      <h4 className="text-sm font-medium text-blue-900">Email Configuration</h4>
                    </div>
                    <p className="text-sm text-blue-700">
                      Emails are sent from the centralized system configuration: <strong>program-tags@tchallasolutions.com</strong>
                    </p>
                    <p className="text-xs text-blue-600 mt-1">
                      All confirmation emails will be sent from "Program Tags" using the system's email settings.
                    </p>
                  </div>

                  {/* Email Subject */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Subject
                    </label>
                    <input
                      type="text"
                      value={settings.notification_email_subject}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        notification_email_subject: e.target.value
                      }))}
                      placeholder="Registration Confirmation - {event_name}"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Use {'{event_name}'} and {'{attendee_name}'} as placeholders
                    </p>
                  </div>

                  {/* Email Settings */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      <RefreshCw className="h-4 w-4 inline mr-1" />
                      Max Retries
                    </label>
                    <input
                      type="number"
                      min="0"
                      max="10"
                      value={settings.notification_max_retries}
                      onChange={(e) => setSettings(prev => ({
                        ...prev,
                        notification_max_retries: parseInt(e.target.value) || 0
                      }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Number of retry attempts for failed emails
                    </p>
                  </div>

                  {/* Content Options */}
                  <div className="space-y-3">
                    <h4 className="text-sm font-medium text-gray-700">Email Content Options</h4>
                    <div className="space-y-2">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={settings.notification_include_qr_code}
                          onChange={(e) => setSettings(prev => ({
                            ...prev,
                            notification_include_qr_code: e.target.checked
                          }))}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">Include QR code for check-in</span>
                      </label>
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={settings.notification_include_event_details}
                          onChange={(e) => setSettings(prev => ({
                            ...prev,
                            notification_include_event_details: e.target.checked
                          }))}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="ml-2 text-sm text-gray-700">Include event details</span>
                      </label>
                    </div>
                  </div>

                  {/* Email Template */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Email Template
                      </label>
                      <div className="flex space-x-2">
                        <button
                          type="button"
                          onClick={() => setShowTemplate(!showTemplate)}
                          className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                        >
                          {showTemplate ? <EyeOff className="h-4 w-4 mr-1" /> : <Eye className="h-4 w-4 mr-1" />}
                          {showTemplate ? 'Hide' : 'Show'} Template
                        </button>
                        <button
                          type="button"
                          onClick={useDefaultTemplate}
                          className="text-sm text-green-600 hover:text-green-800"
                        >
                          Use Default
                        </button>
                      </div>
                    </div>
                    
                    {showTemplate && (
                      <>
                        <textarea
                          value={settings.notification_email_template}
                          onChange={(e) => setSettings(prev => ({
                            ...prev,
                            notification_email_template: e.target.value
                          }))}
                          placeholder="Leave empty to use default template"
                          rows={10}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                        />
                        
                        {/* Available Variables */}
                        <div className="mt-2 p-3 bg-gray-50 rounded-md">
                          <h5 className="text-xs font-medium text-gray-700 mb-2">Available Variables:</h5>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                            {Object.entries(availableVariables).map(([key, description]) => (
                              <div key={key} className="text-gray-600">
                                <code className="bg-gray-200 px-1 rounded">{`{${key}}`}</code>
                                <span className="ml-1">{description}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Test Email Section */}
                  <div className="border-t pt-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-3">Test Email</h4>
                    <div className="flex space-x-3">
                      <input
                        type="email"
                        value={testEmail}
                        onChange={(e) => setTestEmail(e.target.value)}
                        placeholder="Enter email to send test"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                      <button
                        type="button"
                        onClick={handleTestEmail}
                        disabled={sendingTest || !testEmail}
                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                      >
                        {sendingTest ? (
                          <RefreshCw className="h-4 w-4 animate-spin mr-2" />
                        ) : (
                          <Send className="h-4 w-4 mr-2" />
                        )}
                        Send Test
                      </button>
                    </div>
                    
                    {testResult && (
                      <div className={`mt-3 p-3 rounded-md flex items-center ${
                        testResult.success 
                          ? 'bg-green-50 text-green-800' 
                          : 'bg-red-50 text-red-800'
                      }`}>
                        {testResult.success ? (
                          <CheckCircle className="h-4 w-4 mr-2" />
                        ) : (
                          <AlertCircle className="h-4 w-4 mr-2" />
                        )}
                        {testResult.message}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end space-x-3 p-6 border-t bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {saving ? (
              <RefreshCw className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Mail className="h-4 w-4 mr-2" />
            )}
            Save Settings
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailNotificationModal;
